'use client';
import React, { useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import AppLogo from './ui/AppLogo';
import {
  LayoutGrid,
  PlusCircle,
  Download,
  Settings,
  ChevronLeft,
  ChevronRight,
  Flame,
  AlertCircle,
  ChevronDown,
  Plus,
  Check,
  Layers,
} from 'lucide-react';
import { useClaimContext } from '@/contexts/ClaimContext';
import Icon from '@/components/ui/AppIcon';


interface SidebarProps {
  collapsed: boolean;
  onToggleCollapse: () => void;
  mobileOpen: boolean;
  onMobileClose: () => void;
}

const NAV_ITEMS = [
  {
    key: 'nav-inventory',
    label: 'Claim Inventory',
    href: '/',
    icon: LayoutGrid,
  },
  {
    key: 'nav-claims',
    label: 'My Claims',
    href: '/claims',
    icon: Layers,
  },
  {
    key: 'nav-item-entry',
    label: 'Add Items',
    href: '/item-entry',
    icon: PlusCircle,
  },
  {
    key: 'nav-export',
    label: 'Export & Summary',
    href: '/export-claim-summary',
    icon: Download,
  },
  {
    key: 'nav-settings',
    label: 'Settings',
    href: '/settings',
    icon: Settings,
  },
];

export default function Sidebar({
  collapsed,
  onToggleCollapse,
  mobileOpen,
  onMobileClose,
}: SidebarProps) {
  const pathname = usePathname();
  const { claims, activeClaim, unpricedCount, switchClaim, addClaim } = useClaimContext();
  const [claimMenuOpen, setClaimMenuOpen] = useState(false);
  const [addingClaim, setAddingClaim] = useState(false);
  const [newClaimName, setNewClaimName] = useState('');

  const isActive = (href: string) => {
    if (href === '/') return pathname === '/';
    return pathname.startsWith(href);
  };

  const handleAddClaim = async () => {
    const name = newClaimName.trim() || `Claim ${claims.length + 1}`;
    await addClaim(name);
    setNewClaimName('');
    setAddingClaim(false);
  };

  return (
    <aside
      className={[
        'fixed inset-y-0 left-0 z-30 flex flex-col bg-card border-r border-border sidebar-transition',
        'lg:relative lg:translate-x-0',
        collapsed ? 'w-16' : 'w-60',
        mobileOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0',
      ].join(' ')}
    >
      {/* Logo area */}
      <div className={`flex items-center border-b border-border h-16 px-3 ${collapsed ? 'justify-center' : 'gap-3 px-4'}`}>
        <div className="flex items-center gap-2 min-w-0">
          <AppLogo size={32} />
          {!collapsed && (
            <div className="min-w-0">
              <span className="font-semibold text-sm text-foreground block truncate">ClaimTracker</span>
              <span className="text-xs text-muted-foreground block truncate">Fire Loss Inventory</span>
            </div>
          )}
        </div>
        {/* Mobile close */}
        <button
          className="ml-auto p-1 rounded hover:bg-secondary transition-colors lg:hidden"
          onClick={onMobileClose}
          aria-label="Close navigation"
        >
          <ChevronLeft size={16} />
        </button>
      </div>

      {/* Claim selector */}
      {!collapsed && (
        <div className="mx-3 mt-3 relative">
          <button
            onClick={() => setClaimMenuOpen((p) => !p)}
            className="w-full px-3 py-2 rounded-lg bg-warning-bg border border-warning/30 flex items-center gap-2 hover:bg-warning/10 transition-colors"
          >
            <Flame size={14} className="text-warning shrink-0" />
            <div className="flex-1 min-w-0 text-left">
              <p className="text-xs font-medium text-warning truncate">
                {activeClaim ? activeClaim.name : 'No Active Claim'}
              </p>
              {activeClaim?.claimNumber && (
                <p className="text-xs text-muted-foreground truncate">{activeClaim.claimNumber}</p>
              )}
            </div>
            <ChevronDown size={12} className={`text-warning shrink-0 transition-transform ${claimMenuOpen ? 'rotate-180' : ''}`} />
          </button>

          {claimMenuOpen && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-card border border-border rounded-lg shadow-lg z-50 overflow-hidden">
              <div className="max-h-48 overflow-y-auto">
                {claims.map((claim) => (
                  <button
                    key={claim.id}
                    onClick={async () => {
                      await switchClaim(claim.id);
                      setClaimMenuOpen(false);
                    }}
                    className="w-full flex items-center gap-2 px-3 py-2 text-left hover:bg-secondary transition-colors"
                  >
                    <Check size={12} className={claim.isActive ? 'text-primary' : 'opacity-0'} />
                    <span className="text-xs text-foreground truncate flex-1">{claim.name}</span>
                  </button>
                ))}
              </div>
              <div className="border-t border-border p-2">
                {addingClaim ? (
                  <div className="flex gap-1">
                    <input
                      autoFocus
                      type="text"
                      value={newClaimName}
                      onChange={(e) => setNewClaimName(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleAddClaim();
                        if (e.key === 'Escape') { setAddingClaim(false); setNewClaimName(''); }
                      }}
                      placeholder={`Claim ${claims.length + 1}`}
                      className="flex-1 px-2 py-1 text-xs border border-border rounded bg-input focus:outline-none focus:ring-1 focus:ring-ring"
                    />
                    <button
                      onClick={handleAddClaim}
                      className="px-2 py-1 text-xs bg-primary text-primary-foreground rounded hover:bg-amber-700 transition-colors"
                    >
                      Add
                    </button>
                  </div>
                ) : (
                  <button
                    onClick={() => setAddingClaim(true)}
                    className="w-full flex items-center gap-1.5 px-2 py-1.5 text-xs text-muted-foreground hover:text-foreground hover:bg-secondary rounded transition-colors"
                  >
                    <Plus size={12} /> New Claim
                  </button>
                )}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Nav items */}
      <nav className="flex-1 px-2 py-4 space-y-1 overflow-y-auto scrollbar-thin">
        {NAV_ITEMS.map((item) => {
          const Icon = item.icon;
          const active = isActive(item.href);
          return (
            <Link
              key={item.key}
              href={item.href}
              onClick={onMobileClose}
              title={collapsed ? item.label : undefined}
              className={[
                'flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 group relative',
                active
                  ? 'bg-primary/10 text-primary' :'text-secondary-foreground hover:bg-secondary hover:text-foreground',
                collapsed ? 'justify-center' : '',
              ].join(' ')}
            >
              <Icon size={18} className="shrink-0" />
              {!collapsed && <span className="truncate">{item.label}</span>}
              {/* Collapsed tooltip */}
              {collapsed && (
                <span className="absolute left-full ml-2 px-2 py-1 bg-foreground text-background text-xs rounded whitespace-nowrap opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none z-50">
                  {item.label}
                </span>
              )}
            </Link>
          );
        })}
      </nav>

      {/* Unpriced warning — dynamic, only shown when there are actually unpriced items */}
      {!collapsed && unpricedCount > 0 && (
        <div className="mx-3 mb-3 px-3 py-2 rounded-lg bg-negative-bg border border-negative/20 flex items-center gap-2">
          <AlertCircle size={14} className="text-negative shrink-0" />
          <div className="min-w-0">
            <p className="text-xs font-medium text-negative">
              {unpricedCount} {unpricedCount === 1 ? 'item' : 'items'} unpriced
            </p>
            <p className="text-xs text-muted-foreground truncate">Need price research</p>
          </div>
        </div>
      )}

      {/* Collapse toggle */}
      <div className="border-t border-border p-2">
        <button
          onClick={onToggleCollapse}
          className="hidden lg:flex w-full items-center justify-center p-2 rounded-lg hover:bg-secondary transition-colors text-muted-foreground hover:text-foreground"
          aria-label={collapsed ? 'Expand sidebar' : 'Collapse sidebar'}
        >
          {collapsed ? <ChevronRight size={16} /> : (
            <span className="flex items-center gap-2 text-xs">
              <ChevronLeft size={16} />
              <span>Collapse</span>
            </span>
          )}
        </button>
      </div>
    </aside>
  );
}